import { createSlice } from "@reduxjs/toolkit";
import { fetchCompanyList, getCompany } from "./company";
import { companyLogin, fetchAwsUser, logout } from "./auth";
import { fetchApplicationList } from "./application";

interface SharedState {
  status: "idle" | "loading" | "failed";
  menuOpen: "none" | "menu" | "alerts" | "profile";
  toastStatus: {
    open: "success" | "info" | "error" | "none";
    message: string;
  };
  appLoaded: {
    awsUser: boolean;
    companyList: boolean;
    dbUser: boolean;
    appList: boolean;
  };
  globalLoadingState: { isLoading: boolean; loadingCount: number };
  savedRoute?: string;
}

const initialState: SharedState = {
  status: "idle",
  menuOpen: "none",
  toastStatus: {
    open: "none",
    message: "",
  },
  appLoaded: {
    awsUser: false,
    companyList: false,
    dbUser: false,
    appList: false,
  },
  globalLoadingState: { isLoading: false, loadingCount: 0 },
};

const sharedSlice = createSlice({
  name: "shared",
  initialState,
  reducers: {
    toggleMenu(state, action) {
      state.menuOpen = action.payload;
    },
    handleToastState: (state, action) => {
      state.toastStatus = action.payload;
    },
    handleLoadingState: (state, action) => {
      const { type } = action;
      const isLoading = type.endsWith("/pending");

      if (!!isLoading) {
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: state.globalLoadingState.loadingCount + 1,
        };
      } else {
        const newCount =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: newCount > 0,
          loadingCount: newCount,
        };
      }
    },
    updateLoadedState: (state, action) => {
      state.appLoaded = action.payload;
    },
    setSavedRoute(state, action) {
      state.savedRoute = action.payload;
      if (!!action.payload) {
        sessionStorage.setItem("savedRoute", action.payload);
      } else {
        sessionStorage.removeItem("savedRoute");
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAwsUser.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(fetchAwsUser.fulfilled, (state, action) => {
        if (!!action.payload.username) {
          state.appLoaded.awsUser = true;
        }
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchAwsUser.rejected, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })

      .addCase(companyLogin.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(companyLogin.fulfilled, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
        state.appLoaded.dbUser = true;
      })
      .addCase(companyLogin.rejected, (state) => {
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
        state.appLoaded.dbUser = true;
      })
      .addCase(fetchCompanyList.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(fetchCompanyList.fulfilled, (state) => {
        state.appLoaded.companyList = true;
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchCompanyList.rejected, (state) => {
        state.appLoaded.companyList = true;
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })

      .addCase(getCompany.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(getCompany.fulfilled, (state) => {
        state.appLoaded.companyList = true;
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
        state.appLoaded = {
          ...state.appLoaded,
          dbUser: false,
          appList: false,
        };
      })
      .addCase(getCompany.rejected, (state) => {
        state.appLoaded.companyList = true;
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchApplicationList.pending, (state) => {
        let count = state.globalLoadingState.loadingCount + 1;
        state.globalLoadingState = {
          isLoading: true,
          loadingCount: count,
        };
      })
      .addCase(fetchApplicationList.fulfilled, (state) => {
        state.appLoaded.companyList = true;
        state.appLoaded.appList = true;
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(fetchApplicationList.rejected, (state) => {
        state.appLoaded.companyList = true;
        let count =
          state.globalLoadingState.loadingCount > 0
            ? state.globalLoadingState.loadingCount - 1
            : 0;
        state.globalLoadingState = {
          isLoading: count > 0,
          loadingCount: count,
        };
      })
      .addCase(logout.fulfilled, (state) => {
        state.status = "idle";
        state.appLoaded.companyList = false;
        state.appLoaded.awsUser = false;
      });
  },
});

export const {
  toggleMenu,
  handleToastState,
  updateLoadedState,
  setSavedRoute,
} = sharedSlice.actions;

export default sharedSlice.reducer;
